import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function GSKViiVPatientInsightsPage() {
  useSEO({
    title: 'GSK ViiV Patient Insights | Design For',
    description: 'Uncovering patient insights for HIV treatment programmes. Research-driven strategy for pharmaceutical patient engagement and treatment adherence.',
    keywords: 'patient insights, HIV treatment, pharmaceutical research, patient engagement, healthcare strategy',
    canonical: '/work/gsk-viiv-patient-insights',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Uncovering patient insights for HIV treatment programmes',
    description: 'We partnered with GSK ViiV to conduct comprehensive patient research, uncovering insights that inform HIV treatment programmes, patient support services, and pharmaceutical strategy.',
    url: 'https://example.com/work/gsk-viiv-patient-insights',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Global pharmaceutical company - HIV patient insights',
    keywords: 'patient insights, HIV treatment, pharmaceutical research, patient engagement, healthcare strategy',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Transforming how patient insights are gathered, stored and used in a complex environment
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Global pharmaceutical company</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                This global pharmaceutical company faced a significant challenge: patient and HCP insights existed across teams, systems, and markets, but were difficult to access, connect, and act on — particularly in a highly regulated environment. The organisation needed a solution that could work across R&amp;D, medical, and commercial functions while remaining compliant and adaptable.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                Over a 3 month discovery programme, we worked closely with senior leaders and frontline teams to co-design a future ready approach to managing and activating patient insights, blending experience design, AI, and change.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Led hands-on co-creation workshops with 120+ stakeholders across R&D, medical, and commercial',
                  'Conducted 12 in-depth interviews with C-suite leaders',
                  'Established alignment through regular steering sessions with the Chief Patient Officer',
                  'Mapped current and future journeys for 12 key "super users" of patient insights',
                  'Designed an AI-powered patient insights platform proof of concept',
                  'Delivered a clear roadmap across technology, process, and change',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/403472c7ae6592343ba0e169a5753aee.jpeg"
                  alt="GSK ViiV team collaborating on patient insights platform"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/165fa20a0dfc37a91c9a60801c59c2ba.jpeg"
                  alt="AI-powered patient insights platform interface"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
            </div>
          </div>

          {/* Footnote */}
          <p className="mt-12 text-xs text-gray-400 italic">
            This work was delivered leading a consulting team during a previous role.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="gsk-viiv-patient-insights" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
